import { useCallback, useEffect, useState } from "react";
import { GiField } from "react-icons/gi";
import { LuCopyPlus } from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import AjudaTela from "../../components/AjudaTela";
import Layout from "../../components/Layout";
import ModalCadastroLote from "../../components/modais/ModalCadastroLote";
import ModalCadastroPasto from "../../components/modais/ModalCadastroPasto";
import { repositorioLote, repositorioPasto, repositorioRotacaoPasto } from "../../sync/repositories/pecuaria";

const AJUDA = {
  titulo: "Rotação de Pastos",
  descricao:
    "Registre aqui quando um lote de animais entra ou sai de um pasto. Isso permite acompanhar o histórico de uso de cada área e o tempo de permanência dos lotes, ajudando no planejamento do manejo de pastagem.",
  secoes: [
    {
      titulo: "Dados da Rotação",
      campos: [
        {
          nome: "Lote",
          obrigatorio: true,
          explicacao: "O lote de animais que está entrando nesse pasto. Se o lote ainda não existe, use o botão '+' ao lado do campo para cadastrá-lo sem sair desta tela.",
        },
        {
          nome: "Pasto",
          obrigatorio: true,
          explicacao: "O pasto que vai receber o lote nesse período.",
        },
        {
          nome: "Data de Entrada",
          obrigatorio: true,
          explicacao: "Data em que o lote entrou nesse pasto.",
        },
        {
          nome: "Data de Saída",
          obrigatorio: false,
          explicacao: "Deixe em branco enquanto o lote ainda estiver nesse pasto — a rotação aparece como 'Em Andamento' na lista até que essa data seja preenchida.",
        },
        {
          nome: "Observações",
          obrigatorio: false,
          explicacao: "Anotações sobre a condição do pasto, do lote ou qualquer detalhe relevante desse período.",
        },
      ],
    },
  ],
  dicas: [
    "A coluna 'Dias' na listagem é calculada automaticamente a partir da Data de Entrada e da Data de Saída (ou da data de hoje, se a rotação ainda estiver em andamento).",
  ],
};

export default function GestaoRotacaoPastos() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [rotacoes, setRotacoes] = useState([]);
  const [rotacoesFiltradas, setRotacoesFiltradas] = useState([]);
  const [editando, setEditando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  
  // Dados para os selects
  const [lotes, setLotes] = useState([]);
  const [pastos, setPastos] = useState([]);
  const [mostrarModalLote, setMostrarModalLote] = useState(false);
  const [mostrarModalPasto, setMostrarModalPasto] = useState(false);
  
  const [filtros, setFiltros] = useState({
    busca: "",
    data_inicio: "",
    data_fim: ""
  });
  
  const [formData, setFormData] = useState({
    lote: "",
    pasto: "",
    data_entrada: "",
    data_saida: "",
    observacoes: "",
  });

  // Carregar lotes
  const carregarLotes = useCallback(async () => {
    try {
      const response = await repositorioLote.listar();
      const dados = response.data.results || response.data;
      setLotes(Array.isArray(dados) ? dados : []);
    } catch (error) {
      console.error("Erro ao carregar lotes:", error);
      setLotes([]);
    }
  }, []);

  // Carregar pastos
  const carregarPastos = useCallback(async () => {
    try {
      const response = await repositorioPasto.listar();
      const dados = response.data.results || response.data;
      setPastos(Array.isArray(dados) ? dados : []);
    } catch (error) {
      console.error("Erro ao carregar pastos:", error);
      setPastos([]);
    }
  }, []);

  // Carregar rotações
  const carregarRotacoes = useCallback(async () => {
    try {
      setCarregandoLista(true);
      const response = await repositorioRotacaoPasto.listar();
      const dados = response.data.results || response.data;
      setRotacoes(Array.isArray(dados) ? dados : []);
      setRotacoesFiltradas(Array.isArray(dados) ? dados : []);
    } catch (error) {
      console.error("Erro ao carregar rotações:", error);
      alert("Erro ao carregar lista de rotações");
    } finally {
      setCarregandoLista(false);
    }
  }, []);

  useEffect(() => {
    carregarRotacoes();
    carregarLotes();
    carregarPastos();
  }, [carregarRotacoes, carregarLotes, carregarPastos]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validarFormulario = () => {
    if (!formData.lote) {
      alert("Selecione um lote.");
      return false;
    }
    if (!formData.pasto) {
      alert("Selecione um pasto.");
      return false;
    }
    if (!formData.data_entrada) {
      alert("Informe a data de entrada.");
      return false;
    }
    
    // Validar se data_saída é posterior a data_entrada
    if (formData.data_saida && formData.data_saida <= formData.data_entrada) {
      alert("A data de saída deve ser posterior à data de entrada.");
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validarFormulario()) return;
    
    setLoading(true);

    const payload = {
      lote: formData.lote,
      pasto: formData.pasto,
      data_entrada: formData.data_entrada,
      data_saida: formData.data_saida || null,
      observacoes: formData.observacoes || "",
    };

    try {
      if (editando) {
        await repositorioRotacaoPasto.atualizar(editandoId, payload);
        alert("Rotação de pasto atualizada com sucesso!");
        handleCancel();
        carregarRotacoes();
      } else {
        await repositorioRotacaoPasto.criar(payload);
        alert("Rotação de pasto registrada com sucesso!");
        handleCancel();
        carregarRotacoes();
      }
    } catch (error) {
      console.error("Erro:", error);
      const data = error.response?.data;
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const erros = Object.values(data).flat();
        alert(erros.join("\n"));
      } else if (Array.isArray(data)) {
        alert(data.join("\n"));
      } else {
        alert("Erro ao salvar. Verifique os dados e tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (rotacao) => {
    setFormData({
      lote: rotacao.lote?.id || rotacao.lote || "",
      pasto: rotacao.pasto?.id || rotacao.pasto || "",
      data_entrada: rotacao.data_entrada || "",
      data_saida: rotacao.data_saida || "",
      observacoes: rotacao.observacoes || "",
    });
    setEditando(true);
    setEditandoId(rotacao.id);
    setMostrarFormulario(true);
    
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleNovaRotacao = () => {
    handleCancel();
    setMostrarFormulario(true);
  };

  const handleCancel = () => {
    setFormData({
      lote: "",
      pasto: "",
      data_entrada: "",
      data_saida: "",
      observacoes: "",
    });
    setEditando(false);
    setEditandoId(null);
    setMostrarFormulario(false);
  };

  const handleExcluir = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir esta rotação de pasto? Esta ação não pode ser desfeita.")) {
      return;
    }
    
    try {
      await repositorioRotacaoPasto.excluir(id);
      alert("Rotação de pasto excluída com sucesso!");
      carregarRotacoes();
      
      if (editandoId === id) {
        handleCancel();
      }
    } catch (error) {
      console.error("Erro ao excluir:", error);
      alert("Erro ao excluir rotação de pasto.");
    }
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    let filtrados = Array.isArray(rotacoes) ? [...rotacoes] : [];
    
    if (filtros.busca) {
      const buscaLower = filtros.busca.toLowerCase();
      filtrados = filtrados.filter(r => 
        r.lote_nome?.toLowerCase().includes(buscaLower) ||
        r.pasto_nome?.toLowerCase().includes(buscaLower)
      );
    }
    
    if (filtros.data_inicio) {
      filtrados = filtrados.filter(r => r.data_entrada >= filtros.data_inicio);
    }
    
    if (filtros.data_fim) {
      filtrados = filtrados.filter(r => r.data_entrada <= filtros.data_fim);
    }
    
    setRotacoesFiltradas(filtrados);
  };

  const limparFiltros = () => {
    setFiltros({ busca: "", data_inicio: "", data_fim: "" });
    setRotacoesFiltradas(Array.isArray(rotacoes) ? rotacoes : []);
  };

  const formatarData = (data) => {
    if (!data) return "—";
    return new Date(data).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
  };

  const calcularDiasPermanencia = (dataEntrada, dataSaida) => {
    if (!dataEntrada) return null;
    const entrada = new Date(dataEntrada);
    const saida = dataSaida ? new Date(dataSaida) : new Date();
    const diffTime = Math.abs(saida - entrada);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getStatusRotacao = (dataSaida) => {
    if (!dataSaida) {
      return { label: "Em Andamento", color: "bg-green-100 text-green-800" };
    }
    return { label: "Finalizada", color: "bg-gray-100 text-gray-800" };
  };

  return (
    <Layout>
      <div className="p-6 font-medium max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">Gestão de Rotação de Pastos <GiField size={38} /></h2>
            <AjudaTela {...AJUDA} />
          </div>
          {!mostrarFormulario && (
            <button
              onClick={handleNovaRotacao}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center gap-2 shadow-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Nova Rotação
            </button>
          )}
        </div>
        
        {/* Formulário de Cadastro/Edição */}
        {mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">
                {editando ? "Editar Rotação de Pasto" : "Registrar Rotação de Pasto"}
              </h3>
              <button
                onClick={handleCancel}
                className="text-gray-500 hover:text-gray-700"
                title="Fechar formulário"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                {/* Linha 1: Lote e Pasto */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Lote *</label>
                    <div className="flex items-center gap-2">
                      <select
                        name="lote"
                        value={formData.lote}
                        onChange={handleChange}
                        className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        required
                      >
                        <option value="">Selecione um lote...</option>
                        {Array.isArray(lotes) && lotes.length > 0 ? (
                          lotes.map(l => (
                            <option key={l.id} value={l.id}>
                              {l.nome} {l.fazenda_nome ? `- ${l.fazenda_nome}` : ''}
                            </option>
                          ))
                        ) : (
                          <option value="" disabled>Nenhum lote encontrado</option>
                        )}
                      </select>
                      <button
                        type="button"
                        onClick={() => setMostrarModalLote(true)}
                        title="Cadastrar novo lote"
                        className="p-2 hover:bg-gray-200 rounded"
                      >
                        <LuCopyPlus size={26} className="text-green-600 hover:text-green-800" />
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Pasto *</label>
                    <div className="flex items-center gap-2">
                      <select
                        name="pasto"
                        value={formData.pasto}
                        onChange={handleChange}
                        className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        required
                      >
                        <option value="">Selecione um pasto...</option>
                        {Array.isArray(pastos) && pastos.length > 0 ? (
                          pastos.map(p => (
                            <option key={p.id} value={p.id}>
                              {p.nome} {p.fazenda_nome ? `- ${p.fazenda_nome}` : ''}
                            </option>
                          ))
                        ) : (
                          <option value="" disabled>Nenhum pasto encontrado</option>
                        )}
                      </select>
                      <button
                        type="button"
                        onClick={() => setMostrarModalPasto(true)}
                        title="Cadastrar novo pasto"
                        className="p-2 hover:bg-gray-200 rounded"
                      >
                        <LuCopyPlus size={26} className="text-green-600 hover:text-green-800" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Linha 2: Datas */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Data de Entrada *</label>
                    <input
                      type="date"
                      name="data_entrada"
                      value={formData.data_entrada}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300 w-64"
                      required
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Data de Saída</label>
                    <input
                      type="date"
                      name="data_saida"
                      value={formData.data_saida}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300 w-64"
                    />
                    <span className="text-xs text-gray-500 mt-1">
                      Deixe em branco se o lote ainda está neste pasto
                    </span>
                  </div>
                </div>

                {/* Linha 3: Observações */}
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Observações</label>
                  <textarea
                    name="observacoes"
                    value={formData.observacoes}
                    onChange={handleChange}
                    rows={4}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                    placeholder="Informações adicionais sobre a rotação (qualidade do pasto, condições, etc.)..."
                  />
                </div>

                {/* Botões do formulário */}
                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors disabled:bg-blue-300"
                  >
                    {loading ? "Salvando..." : (editando ? "Atualizar" : "Registrar")}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}
        
        {/* Seção de Filtros - Sempre visível quando não está editando */}
        {!mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Filtros de Busca
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">Buscar por Lote ou Pasto</label>
                <input
                  type="text"
                  placeholder="Nome do lote ou pasto..."
                  value={filtros.busca}
                  onChange={(e) => setFiltros({...filtros, busca: e.target.value})}
                  className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                />
              </div>
              
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">Data Início (Entrada)</label>
                <input
                  type="date"
                  value={filtros.data_inicio}
                  onChange={(e) => setFiltros({...filtros, data_inicio: e.target.value})}
                  className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                />
              </div>
              
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">Data Fim (Entrada)</label>
                <input
                  type="date"
                  value={filtros.data_fim}
                  onChange={(e) => setFiltros({...filtros, data_fim: e.target.value})}
                  className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-4">
              <button
                onClick={aplicarFiltros}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
              >
                Aplicar Filtros
              </button>
              <button
                onClick={limparFiltros}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
              >
                Limpar Filtros
              </button>
            </div>
          </div>
        )}

        {/* Tabela de Rotações - Sempre visível quando não está editando */}
        {!mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <p className="text-gray-600">
                {Array.isArray(rotacoesFiltradas) ? rotacoesFiltradas.length : 0} rotação(ões) encontrada(s)
              </p>
            </div>
            
            {carregandoLista ? (
              <div className="p-8 text-center text-gray-500">Carregando...</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Código</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Lote</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Pasto</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Data Entrada</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Data Saída</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Dias</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Status</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Observações</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(rotacoesFiltradas) && rotacoesFiltradas.map((rotacao) => {
                      const status = getStatusRotacao(rotacao.data_saida);
                      const dias = calcularDiasPermanencia(rotacao.data_entrada, rotacao.data_saida);
                      return (
                        <tr key={rotacao.id} className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                          <td className="p-3 text-sm font-mono">{rotacao.id}</td>
                          <td className="p-3 text-sm font-medium">
                            <span className="flex items-center gap-1">
                              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                              </svg>
                              {rotacao.lote_nome || `Lote ${rotacao.lote}`}
                            </span>
                          </td>
                          <td className="p-3 text-sm">
                            <span className="flex items-center gap-1">
                              <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                              </svg>
                              {rotacao.pasto_nome || `Pasto ${rotacao.pasto}`}
                            </span>
                          </td>
                          <td className="p-3 text-sm">{formatarData(rotacao.data_entrada)}</td>
                          <td className="p-3 text-sm">{formatarData(rotacao.data_saida)}</td>
                          <td className="p-3 text-sm">{dias} dias</td>
                          <td className="p-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>
                              {status.label}
                            </span>
                          </td>
                          <td className="p-3 text-sm max-w-xs truncate">{rotacao.observacoes || "—"}</td>
                          <td className="p-3">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEditar(rotacao)}
                                className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm transition-colors"
                                title="Editar"
                              >
                                Editar
                              </button>
                              <button
                                onClick={() => handleExcluir(rotacao.id)}
                                className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition-colors"
                                title="Excluir"
                              >
                                Excluir
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    
                    {(!Array.isArray(rotacoesFiltradas) || rotacoesFiltradas.length === 0) && (
                      <tr>
                        <td colSpan="9" className="p-8 text-center text-gray-500">
                          {filtros.busca || filtros.data_inicio || filtros.data_fim
                            ? "Nenhuma rotação encontrada com os filtros selecionados."
                            : "Nenhuma rotação de pasto registrada."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      <ModalCadastroLote
        isOpen={mostrarModalLote}
        onClose={() => setMostrarModalLote(false)}
        onSuccess={async (novo) => {
          await carregarLotes();
          setFormData((prev) => ({ ...prev, lote: novo.id }));
        }}
      />
      <ModalCadastroPasto
        isOpen={mostrarModalPasto}
        onClose={() => setMostrarModalPasto(false)}
        onSuccess={async (novo) => {
          await carregarPastos();
          setFormData((prev) => ({ ...prev, pasto: novo.id }));
        }}
      />
    </Layout>
  );
}